/**
 * Sketchbook store — saved sketches index + autosave (brief §7/§18).
 */
import { getSketchRepository } from '@/services/sketchRepository';
import { useLayersStore } from '@/stores/layers';
import { usePatternStore } from '@/stores/pattern';
import { useToolsStore } from '@/stores/tools';
import type { SketchDocument, SketchMeta } from '@/types/drawing';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

/** localStorage key for the last-open sketch, reopened on return. */
const LAST_KEY = 'esch_last_sketch';

function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const useSketchbookStore = defineStore('sketchbook', () => {
  const sketches = ref<SketchMeta[]>([]);
  const currentSketchId = ref<string | null>(null);
  /** true while saves are failing on quota (web) — drives the storage banner */
  const storageWarning = ref(false);

  const repo = getSketchRepository();

  // Remember the last-open sketch so it can be reopened on the next visit.
  watch(currentSketchId, (id) => {
    try {
      if (id) localStorage.setItem(LAST_KEY, id);
      else localStorage.removeItem(LAST_KEY);
    } catch {
      // best-effort
    }
  });

  /** The id stored from a previous visit (may no longer exist). */
  function lastSketchId(): string | null {
    try {
      return localStorage.getItem(LAST_KEY);
    } catch {
      return null;
    }
  }

  const currentName = computed(
    () => sketches.value.find((m) => m.id === currentSketchId.value)?.name ?? 'Untitled',
  );

  async function refresh(): Promise<void> {
    sketches.value = await repo.list();
  }

  async function newSketch(patternId: string, variantId: string, opts: { keepContent?: boolean } = {}): Promise<string> {
    const layers = useLayersStore();
    const patternStore = usePatternStore();
    const tools = useToolsStore();
    const now = Date.now();
    const meta: SketchMeta = {
      id: newId(),
      name: 'Untitled',
      patternId,
      variantId,
      artistTile: patternStore.currentConfig.artistTile,
      latticeParams: patternStore.currentConfig.latticeParams,
      colourCount: tools.colourCount,
      baseColourIndex: tools.baseColourIndex,
      thumbnail: null,
      createdAt: now,
      updatedAt: now,
    };
    if (!opts.keepContent) layers.clearAll();
    currentSketchId.value = meta.id;
    await persist(meta);
    return meta.id;
  }

  async function openSketch(id: string): Promise<void> {
    const doc = await repo.load(id);
    const layers = useLayersStore();
    await layers.loadContent({
      reference: doc.reference,
      sketchLayers: doc.sketch.layers,
      inkLayers: doc.ink?.layers, // Bug 4 — ink raster layers, separate from sketch
      traceLayers: doc.trace?.layers,
      paths: doc.ink?.paths, // legacy fallback — migrated when no trace.layers
    });
    currentSketchId.value = id;
  }

  async function saveCurrent(): Promise<void> {
    const id = currentSketchId.value;
    if (!id) return;
    const existing = sketches.value.find((m) => m.id === id);
    if (!existing) return;
    const patternStore = usePatternStore();
    const tools = useToolsStore();
    await persist({
      ...existing,
      patternId: patternStore.currentPatternId,
      variantId: patternStore.currentVariantId,
      artistTile: patternStore.currentConfig.artistTile,
      latticeParams: patternStore.currentConfig.latticeParams,
      colourCount: tools.colourCount,
      baseColourIndex: tools.baseColourIndex,
      updatedAt: Date.now(),
    });
  }

  async function persist(meta: SketchMeta): Promise<void> {
    const layers = useLayersStore();
    const doc: SketchDocument = {
      meta: { ...meta, thumbnail: layers.renderThumbnail() },
      reference: {
        imageDataUrl: layers.reference.imageDataUrl,
        transform: { ...layers.reference.transform },
        opacity: layers.reference.opacity,
        visible: layers.reference.visible,
      },
      sketch: {
        layers: layers.serializeLayers(),
      },
      // `paths` retained empty for backward compat (Trace layers are the vector
      // source now); `layers` are the ink raster layers (Bug 4).
      ink: { paths: [], layers: layers.serializeInkLayers() },
      trace: { layers: layers.serializeTraceLayers() },
    };
    let result = await repo.save(doc);
    if (!result.ok && doc.meta.thumbnail) {
      // Near quota — drop the thumbnail and retry once (§18).
      result = await repo.save({ ...doc, meta: { ...doc.meta, thumbnail: null } });
    }
    if (result.ok) {
      storageWarning.value = false;
      layers.isDirty = false;
      await refresh();
    } else {
      // In-memory work stays intact; banner shows; autosave will retry (§18).
      storageWarning.value = true;
    }
  }

  async function deleteSketch(id: string): Promise<void> {
    await repo.remove(id);
    if (currentSketchId.value === id) currentSketchId.value = null;
    await refresh();
  }

  /** Delete several sketches at once (gallery multi-select). */
  async function deleteMany(ids: readonly string[]): Promise<void> {
    for (const id of ids) {
      await repo.remove(id);
      if (currentSketchId.value === id) currentSketchId.value = null;
    }
    await refresh();
  }

  async function renameSketch(id: string, name: string): Promise<void> {
    const meta = sketches.value.find((m) => m.id === id);
    if (!meta) return;
    if (id === currentSketchId.value) {
      await persist({ ...meta, name, updatedAt: Date.now() });
    } else {
      const doc = await repo.load(id);
      doc.meta = { ...doc.meta, name, updatedAt: Date.now() };
      const result = await repo.save(doc);
      storageWarning.value = !result.ok;
      await refresh();
    }
  }

  /** Rename the open sketch, creating it first if nothing is saved yet. */
  async function renameCurrent(name: string): Promise<void> {
    if (!currentSketchId.value) {
      const patternStore = usePatternStore();
      await newSketch(patternStore.currentPatternId, patternStore.currentVariantId, {
        keepContent: true,
      });
    }
    if (currentSketchId.value) await renameSketch(currentSketchId.value, name);
  }

  return {
    sketches,
    currentSketchId,
    currentName,
    storageWarning,
    refresh,
    newSketch,
    openSketch,
    saveCurrent,
    deleteSketch,
    deleteMany,
    renameSketch,
    renameCurrent,
    lastSketchId,
  };
});
